import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface AccordionItem {
  id: string;
  title: string;
  subtitle?: string;
  content: React.ReactNode;
  defaultOpen?: boolean;
}

interface AccordionProps {
  items: AccordionItem[];
  className?: string;
  allowMultiple?: boolean;
}

export const Accordion: React.FC<AccordionProps> = ({
  items,
  className = '',
  allowMultiple = false
}) => {
  const [openItems, setOpenItems] = useState<Set<string>>(
    new Set(items.filter(item => item.defaultOpen).map(item => item.id))
  );

  const toggleItem = (itemId: string) => {
    setOpenItems(prev => {
      const newSet = new Set(prev);
      if (allowMultiple) {
        if (newSet.has(itemId)) {
          newSet.delete(itemId);
        } else {
          newSet.add(itemId);
        }
      } else {
        if (newSet.has(itemId)) {
          newSet.clear();
        } else {
          newSet.clear();
          newSet.add(itemId);
        }
      }
      return newSet;
    });
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {items.map((item) => {
        const isOpen = openItems.has(item.id);

        return (
          <div
            key={item.id}
            className="border border-neutral-200 rounded-lg overflow-hidden bg-white"
          >
            <button
              onClick={() => toggleItem(item.id)}
              className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-neutral-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset"
            >
              <div className="flex-1">
                <h4 className="font-medium text-neutral-900">{item.title}</h4>
                {item.subtitle && (
                  <p className="text-sm text-neutral-600 mt-1">{item.subtitle}</p>
                )}
              </div>
              <div className="ml-4 flex-shrink-0">
                {isOpen ? (
                  <ChevronDownIcon className="h-5 w-5 text-neutral-500 transition-transform duration-200" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-neutral-500 transition-transform duration-200" />
                )}
              </div>
            </button>

            {isOpen && (
              <div className="border-t border-neutral-200 bg-neutral-50">
                <div className="p-4">
                  {item.content}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const ServiceAccordion: React.FC<{
  services: Array<{
    name: string;
    description?: string;
    totalPrice?: number;
    unitPrice?: number;
    quantity?: number;
    items?: Array<{
      name: string;
      description?: string;
      quantity?: number;
      unitPrice?: number;
      totalPrice?: number;
    }>;
  }>;
  className?: string;
}> = ({ services, className = '' }) => {
  const accordionItems: AccordionItem[] = services.map((service, index) => ({
    id: `service-${index}`,
    title: service.name,
    subtitle: service.description,
    content: (
      <div className="space-y-3">
        {/* Service details */}
        <div className="flex justify-between items-center p-3 bg-white rounded-lg border border-neutral-200">
          <div>
            <p className="font-medium text-neutral-900">{service.name}</p>
            {service.description && (
              <p className="text-sm text-neutral-600 mt-1">{service.description}</p>
            )}
            {service.quantity && service.quantity > 1 && (
              <p className="text-xs text-neutral-500 mt-1">
                Quantity: {service.quantity} × RM {service.unitPrice || 0}
              </p>
            )}
          </div>
          <p className="font-semibold text-primary-600">
            RM {service.totalPrice || service.unitPrice || 0}
          </p>
        </div>

        {/* Service items if available */}
        {service.items && service.items.length > 0 && (
          <div className="ml-4 space-y-2">
            <h5 className="text-sm font-medium text-neutral-700 mb-2">Included Items:</h5>
            {service.items.map((item, itemIndex) => (
              <div key={itemIndex} className="flex justify-between items-center p-2 bg-white rounded border border-neutral-100">
                <div>
                  <p className="text-sm font-medium text-neutral-800">{item.name}</p>
                  {item.description && (
                    <p className="text-xs text-neutral-500">{item.description}</p>
                  )}
                  {item.quantity && item.quantity > 1 && (
                    <p className="text-xs text-neutral-400">
                      {item.quantity} × RM {item.unitPrice || 0}
                    </p>
                  )}
                </div>
                <p className="text-sm font-medium text-neutral-700">
                  RM {item.totalPrice || item.unitPrice || 0}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    ),
    defaultOpen: index === 0 // Open first service by default
  }));

  return <Accordion items={accordionItems} className={className} allowMultiple={true} />;
};